import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import z from "zod";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES, REASON_GROUPS_NAMES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { createUUID } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";

const schema = z.object({
  holdReasons: z
    .array(
      z.object({
        value: z
          .string()
          .min(3, "Reason must be at least 3 characters long")
          .max(100, "Reason must not exceed 100 characters")
          .trim(),
      })
    )
    .superRefine((items, ctx) => {
      const uniqueValues = new Map();
      items.forEach((item, index) => {
        const firstIndex = uniqueValues.get(item.value);
        if (firstIndex !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Reason must be unique`,
            path: [index, "value"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Reason must be unique`,
            path: [firstIndex, "value"],
          });
          return;
        }
        uniqueValues.set(item.value, index);
      });
    }),
});

const getReasonsUUID = createUUID();

export const useHoldReasonsViewModel = ({ orderTemplate }) => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const [holdReasonOptions, setHoldReasonOptions] = useState([]);
  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      holdReasons:
        orderTemplate.holdReasons?.map((reason) => ({
          value: reason,
        })) || [],
    },
    resolver: zodResolver(schema),
  });
  const holdReasonsForm = useFieldArray({
    control: methods.control,
    name: "holdReasons",
  });

  const getHoldReasons = () => {
    stateManager.abort(getReasonsUUID);
    stateManager.run(async () => {
      const response = await taskService.reasonGroups.getReasonsByName({
        name: REASON_GROUPS_NAMES.ORDER_TEMPLATE_HOLD,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setHoldReasonOptions(response.data?.map(({ reason }) => reason));
      }
    }, getReasonsUUID);
  };

  const handleReset = () => {
    methods.reset({
      holdReasons:
        orderTemplate.holdReasons?.map((reason) => ({
          value: reason,
        })) || [],
    });
  };

  return {
    isLoading,
    holdReasonOptions,
    methods,
    holdReasonsForm,
    getHoldReasons,
    handleReset,
  };
};
